// src/integration/growth/index.ts — growth-SDK orchestrator (the single entry point).
//
// initGrowth() is called once at boot AFTER loadGrowthConfig() has merged the served flags. It inits each
// growth SDK ONLY when (a) that SDK's interlock gate is green AND (b) we're on a native platform. Every
// init is independently gated + try/caught, so one SDK can never break boot or another. On web (no
// Capacitor global) it returns immediately, so the web bundle never references a native plugin and stays
// firewall-clean. This file is the "wiring": the adapters are inert stubs until their packages are
// installed (ACTIVATION), at which point only the adapter bodies change — not this gate logic.

import { isSurfaceLive, isGrowthSdkAllowed } from '../../config/monetization';
import { initCrash } from './crash';
import { initAttribution } from './attribution';
import { initAds } from './ads';
import { initIap } from './iap';
import type { GrowthInitResult } from './types';

let started = false;

/** True only inside a Capacitor native shell. Uses the injected global so we don't pull @capacitor/core
 *  into the web bundle (and so web/dev/headless always reads false → everything stays dark). */
function isNativePlatform(): boolean {
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return cap?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

async function safe(label: string, fn: () => Promise<GrowthInitResult>): Promise<void> {
  try {
    await fn();
  } catch {
    // A growth SDK failing to init must NEVER break the game. Swallow (label kept for a future dev log).
    void label;
  }
}

/**
 * Initialize whatever growth SDKs are currently allowed. Idempotent (first call wins) and safe to call
 * fire-and-forget. With every flag at its frozen-OFF default — or on web — this is a complete no-op.
 */
export async function initGrowth(): Promise<void> {
  if (started) return;
  started = true;
  if (!isNativePlatform()) return; // web/dev/headless: nothing to init, stays dark + firewall-clean
  if (isGrowthSdkAllowed('crash')) await safe('crash', initCrash);
  if (isGrowthSdkAllowed('attribution')) await safe('attribution', initAttribution);
  if (isSurfaceLive('rewardedAd')) await safe('ads', initAds);
  if (isSurfaceLive('subscription') || isSurfaceLive('cosmeticIap')) await safe('iap', initIap);
}
